import { inject, Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ApiResponse } from '@shared/models/securepay';
import {
  BusinessInformationResponse,
  DocumentResponseDTO,
} from '../../core/models/kyc.model';
import { BankService } from '@shared';

export interface OwnersResponseData {
  businessApprovalStatus: string;
  approvalComment: string | null;
  owners: any[];
}

@Injectable({
  providedIn: 'root',
})
export class KycService {
  private baseUrl = environment.baseAPI;
  private http = inject(HttpClient);
  private bankService = inject(BankService);

  register(payload: any) {
    const data = {
      ...payload,
      businesstypes: Number(payload.businesstypes),
      businessRegistrationType: Number(payload.businessRegistrationType),
    };
    return this.http.post(
      `${this.baseUrl}/api/Registration/user-registration/v2`,
      data,
    );
  }

  getBusinessInformation(userId: string) {
    return this.http.get<ApiResponse<BusinessInformationResponse>>(
      `${this.baseUrl}/api/kyc/businessInformation/${userId}`,
    );
  }

  submitBusinessInfo(payload: any) {
    const formData = new FormData();
    Object.keys(payload).forEach((key) => {
      formData.append(key, payload[key]);
    });
    return this.http.post(`${this.baseUrl}/api/kyc/complete-info`, formData);
  }

  submitBusinessInformation(payload: any) {
    return this.http.post<ApiResponse<null>>(
      `${this.baseUrl}/api/kyc/business-information`,
      payload,
    );
  }

  submitBusinessVerification(payload: any[]) {
    const formData = new FormData();
    payload.forEach((item, index) => {
      Object.keys(item).forEach((key) => {
        const value = item[key];
        if (value instanceof File) {
          formData.append(
            `businessOwner${index + 1}.${key}`,
            value,
            value.name,
          );
        } else {
          formData.append(`businessOwner${index + 1}.${key}`, value);
        }
      });
    });
    return this.http.post<ApiResponse<null>>(
      `${this.baseUrl}/api/kyc/owner-info`,
      formData,
    );
  }

  submitBankInformation(payload: any) {
    return this.http.put<ApiResponse<null>>(
      `${this.baseUrl}/api/kyc/bank-info`,
      payload,
    );
  }

  submitOnlineInfo(payload: any) {
    return this.http.put<ApiResponse<null>>(
      `${this.baseUrl}/api/kyc/online-info`,
      payload,
    );
  }

  review() {
    return this.http.post<ApiResponse<null>>(
      `${this.baseUrl}/api/kyc/info-completion`,
      {},
    );
  }

  getOwners() {
    return this.http.get<ApiResponse<OwnersResponseData>>(`${this.baseUrl}/api/kyc/owners`);
  }

  getBankList() {
    return this.bankService.getBankList(this.baseUrl);
  }

  getDocuments() {
    return this.http.get<ApiResponse<DocumentResponseDTO>>(
      `${this.baseUrl}/api/kyc/documents`,
    );
  }

  uploadDocuments(payload: any) {
    const formData = new FormData();
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      if (typeof value === 'string') {
        formData.append(`${key}`, value);
      } else if (value instanceof File) {
        formData.append(`${key}`, value, value.name);
      } else if (Array.isArray(value) && value[0] instanceof File) {
        value.forEach((file) => formData.append(`${key}`, file, file.name));
      }
    });
    return this.http.post(`${this.baseUrl}/api/kyc/documentation`, formData);
  }
}
